import { toast } from "@/components/common/Toast/Toast"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { ChevronDown } from "lucide-react"
import { Fragment, useEffect, useState } from "react"
import {
  useApplyStatementPoolScope,
  useEditStatementVerdict,
  useReviewStatement,
  useUpdateStatementScope,
} from "../../../../../api/declarationsApi"
import type {
  CandidateArticle,
  IssuerScopeProposal,
  ReaderHint,
  UncoveredClaim,
} from "../../../../../api/types"
import type { WorklistIssue } from "../../studio/cellState"
import type { Verdict } from "../../studio/studioTypes"
import { issueReasonKey } from "../footer/issueReason"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import {
  deriveGeltung,
  scopeWriteFor,
  type Geltung,
} from "../mapping/geltungMapping"
import { hasConditionsRow } from "../mapping/verdictDisplay"
import type { BrowserView } from "../scope/ArticleBrowser"
import {
  hasIssuerScopeNote,
  IssuerScopeCardNote,
} from "../scope/IssuerScopeNote"
import type { StatementCard } from "../useStudioV2State"
import { ArticleRow } from "./ArticleRow"
import { EditionRow } from "./EditionRow"
import { EvidenceList } from "./EvidenceList"
import { GeltungSelect } from "./GeltungSelect"
import {
  CountPill,
  geltungSentence,
  headSubjectCount,
  headTokenOrder,
  headVerb,
  verdictSentence,
  type HeadToken,
} from "./HeaderSentence"
import { RemoveStatementControl } from "./RemoveStatementControl"
import { ResetStatementButton } from "./ResetStatementButton"
import { SubstanceRow } from "./SubstanceRow"
import { VerdictSelect } from "./VerdictSelect"

export interface UnitCardProps {
  declId: string
  index: number
  card: StatementCard
  /** The declaration's whole resolved article pool, WITH each article's own
   *  current state (`name`, `matchConfidence`): the header sentence's "von
   *  330" total and `scopeWriteFor`'s "Alle Artikel" write target
   *  (`poolNumbers`, derived below). */
  pool: CandidateArticle[]
  /** `useStudioV2State`'s `uncoveredClaimsByArticle`, keyed by article
   *  number: threaded to the article row's marker and, for a blanket
   *  ("all") card, the quiet count note. Optional, defaults empty. */
  uncoveredClaimsByArticle?: Map<string, UncoveredClaim[]>
  /** The active Verordnung's inquiry template reader hints, keyed by article
   *  number: threaded to the article row's notes. Optional, defaults empty. */
  readerHintsByArticle?: Map<string, ReaderHint[]>
  /** Lands a reader hint's sheet row (1-based) in the workbook pane
   *  (`StudioV2Layout`'s own `handleJumpToSheetRow`). Omitted (a PDF): the
   *  hint lines stay plain notes. */
  onJumpToSheetRow?: (sheet: string, row: number) => void
  /** True on a workbook declaration — `CellQuote.page` there is a SURFACE
   *  ORDINAL into the workbook's sheet catalog, never a PDF page number
   *  (`DeclarationReadModel`'s own doc), and workbook evidence contributes
   *  no `evidence` anchors at all (`to_evidence` excludes cells; the cell
   *  channel is `(card.cells ?? [])` instead). Switches the evidence list
   *  and the header jump to the cell channel. Defaults false. */
  isWorkbook?: boolean
  /** The card header / an evidence row → scroll the document pane there AND
   *  flash the statement's own evidence quotes, exactly like v1's citation
   *  click (`StudioScreenA::showAnchor`) — see `StudioV2Layout
   *  ::handleJumpToEvidence`, the sole caller. Takes the whole card (not
   *  just its page) because the flash needs the FULL `evidence` array, not
   *  only the first anchor's page. ALSO takes this card's own 1-based render
   *  `index`: the flash's `SourceRegion.label` feeds the PowerBar's
   *  picker/ruler tooltip once the bar opens, and a raw statement id there
   *  would read as a bare UUID to the reviewer. Omitted (no evidence
   *  affordance rendered, header click inert) when the caller has nowhere
   *  to route it, never expected in practice since `StudioV2Layout` always
   *  supplies one. */
  onJumpToEvidence?: (
    card: StatementCard,
    index: number,
    evidenceIndex?: number,
  ) => void
  /** The active Verordnung's Referenzlisten-Achse (`state.editionInfo`) —
   *  drives the card's REFERENZLISTE row (`EditionRow`). Null/omitted (a
   *  Verordnung without an edition catalogue, or an older caller) renders
   *  no row at all. */
  editionInfo?: FrameworkEditionInfo | null
  /** The active Verordnung's display name (`ReviewMaskPane`'s own
   *  `frameworkName`), passed straight through to `ArticleRow`'s AskBox
   *  (TUR-1211 collector prefill). See `ArticleRowProps.frameworkName`'s own
   *  doc for the fallback every caller predating this prop keeps. */
  frameworkName?: string
  /** This card is the one an approve-gate / open-points jump or a fresh
   *  create landed on (`ReviewMaskPane`'s `focusedStatementId`): it opens
   *  itself, so the reviewer never lands on a collapsed row. */
  focused?: boolean
  /** The open/closed state, owned by the caller when given (`ReviewMaskPane`
   *  keeps one flag per card id): the card itself unmounts on a regulation
   *  switch and whenever a mask sheet replaces the list, so a local flag
   *  came back collapsed each time (live test, wave 5). Standalone renders
   *  and tests may leave both out and get the old local behaviour. */
  expanded?: boolean
  onExpandedChange?: (expanded: boolean) => void
  /** Bumps on every jump (`ReviewMaskPane`'s own prop doc), so re-focusing
   *  an already focused card still re-opens it. */
  focusNonce?: number
  /** The step the jump that landed here named (`WorklistIssue.step`, off the
   *  URL's `&step=`, `StudioV2Layout::goToOpenPoint`'s own doc): on focus,
   *  step 2 also opens the article browser, step 3 also opens the substances
   *  editor, step 1 (or null/omitted, every existing caller) only expands,
   *  so a reviewer sent to "fix the scope" lands IN the browser, not merely
   *  on the card that owns it. */
  focusStep?: 1 | 2 | 3 | null
  /** This statement's own open worklist issues, in DISPLAY order (lowest
   *  step first, `openIssuesByCard`'s own doc): the header wears them as
   *  a reason chip ("2 offen · Artikelbezug noch nicht angewendet", or just
   *  the reason for a single issue) and names the count in the chevron's
   *  tooltip. */
  openIssues?: readonly WorklistIssue[]
  /** How many statement cards the active Verordnung renders
   *  (`ReviewMaskPane`'s own `cards.length`) — read ONLY by the step-1
   *  chip's redundancy rule below. Omitted (a caller with no card list of
   *  its own, every standalone render and test predating the rule) means
   *  "unknown", and an unknown count never suppresses anything. */
  statementCount?: number
  /** Is "Verordnung als geprüft markieren" below the cards actually
   *  PRESSABLE right now (`ReviewMaskPane`'s own `blockedByInquiry`)? Read
   *  ONLY by the step-1 chip's redundancy rule: that rule hands the reviewer
   *  to this gesture, so it may not fire while the gesture is refusing.
   *  Omitted (every caller with no footer of its own) means "unknown", which
   *  is not a usable button, so the chip stays. */
  conformAvailable?: boolean
  /** Opens the article browser for THIS statement's scope in place of the
   *  cards (`ReviewMaskPane`'s sheet): from the header's count pill, the
   *  ARTIKEL row and the Geltung pick "Artikelliste". Omitted (a caller with
   *  no pane to host the browser, every test predating it): the pill stays
   *  a plain label and the pick actions are not offered. */
  onOpenArticleBrowser?: (view: BrowserView, geltung: Geltung) => void
  /** Opens the substance table in place of the cards (`ReviewMaskPane`'s
   *  sheet, round 6): from a STOFFE chip (that substance's rows start open)
   *  or the row's edit button (null). Omitted, same convention as
   *  `onOpenArticleBrowser` above: the chips stay plain labels and the edit
   *  button is not offered. */
  onOpenSubstances?: (focusKey: string | null) => void
  /** MC_ISSUER_SCOPE_PROPOSAL (TUR-1720), straight through to `ArticleRow`. */
  issuerProposal?: IssuerScopeProposal | null
}

/**
 * TUR-1211: the open-points chip on a collapsed statement card, parked.
 *
 * Tester feedback 18.09: the copy counts the open points but names only
 * the first one ("2 offen · Aussage noch nicht bestätigt"), which reads on
 * the card as a contradiction rather than a summary. Ferdi decided it
 * comes off until the redesign lands:
 * https://app.notion.com/p/3df55b45c45281c1894cccf6cc849f40
 *
 * Deliberately a constant and not a feature flag: this is a days-long parking
 * of finished UI, not a rollout, and the redesign flips this one line back.
 * Everything the chip derives from stays wired and keeps its own coverage
 * (`issueReasonKey`, `openChipText`, `chipRedundantWithConformButton`), and
 * the count itself never left the card: the chevron's tooltip names it
 * (`MC_DECL_V2_UNIT_OPEN_POINTS`), as does the footer's open-points map.
 *
 * Typed `boolean` on purpose: a bare `false` narrows to the literal type and
 * turns every use below into a constant condition the linter then flags.
 */
const SHOW_OPEN_CHIP: boolean = false

/**
 * One statement, editable (review-mask prototype port). The header is ONE
 * readable sentence on ONE row ("300 von 330 Artikeln sind laut Liste
 * konform"), built from the count pill, the verb, the Geltung select and the
 * verdict select (`HeaderSentence.tsx`; a container query in studioV2.css
 * shortens the tokens before the row could wrap). Every card starts
 * COLLAPSED to that sentence, so the reviewer sees every extracted statement
 * at once; the chevron (or a focus jump) opens the body: REFERENZLISTE
 * (`EditionRow`) → ARTIKEL (`ArticleRow`, the article browser behind its
 * pill and the Rückfrage composer while the scope or the verdict is unclear)
 * → STOFFE (`SubstanceRow`, only while the verdict names an exception, the
 * substance table behind its chips) → the quotes, behind their own "n
 * Fundstellen anzeigen" link.
 *
 * A click anywhere on the open card's header row expands or collapses it,
 * same as the chevron (`handleHeaderRowClick`, product ask 2026-09-17: not
 * just the small arrow), and still jumps to the evidence when there is any;
 * the chevron and the open-points chip (`toggleAndJump`) are keyboard-
 * reachable too. The excluded quiet row keeps its own narrower gesture
 * (`headerJump`), see its own doc.
 */
export function UnitCard({
  declId,
  index,
  card,
  pool,
  uncoveredClaimsByArticle,
  readerHintsByArticle,
  onJumpToSheetRow,
  isWorkbook = false,
  onJumpToEvidence,
  editionInfo = null,
  frameworkName,
  focused = false,
  expanded: expandedProp,
  onExpandedChange,
  focusNonce = 0,
  focusStep = null,
  openIssues = [],
  statementCount,
  conformAvailable = false,
  onOpenArticleBrowser,
  onOpenSubstances,
  issuerProposal = null,
}: UnitCardProps) {
  const { translate } = useTranslation()
  const editVerdict = useEditStatementVerdict()
  const updateScope = useUpdateStatementScope()
  const applyPool = useApplyStatementPoolScope()
  const reviewStatement = useReviewStatement()
  const resetEnabled = useFeatureFlag("MC_STATEMENT_RESET")
  const poolNumbers = pool.map((a) => a.articleNumber)
  const isBase = index === 0

  // W5-4: the open-points chip, count plus the FIRST (most urgent, lowest
  // step) open issue's own reason, reusing the gate/popover's own
  // `issueReasonKey` mapping so the chip never disagrees with what the map
  // or the gate says about the same statement. `openIssues` arrives already
  // in display order (`openIssuesByCard`'s own doc), so `[0]` IS the first.
  // W5 step3-chip: `firstOpenReason` also carries the hover explanation
  // (`hint`, null when no V3 gesture clears that state), rendered below the
  // chip.
  const openCount = openIssues.length
  const firstOpenIssue = openIssues[0]
  const firstOpenReason = firstOpenIssue
    ? issueReasonKey(firstOpenIssue, card.verdict)
    : null
  // Tester feedback 17.09: on a Verordnung with ONE statement, a lone step-1
  // `needs-verification` point is exactly what "Verordnung als geprüft
  // markieren" below the cards already offers to clear, and with a single
  // card on screen there is no question which statement that button means.
  // The chip would repeat the button and say nothing else, so it goes.
  //
  // Narrow on purpose, each condition carries its own reason:
  //   - `statementCount === 1`: with a second card the button no longer
  //     names one statement, so the chip is the only thing that does.
  //   - `openCount === 1`: a second point gives the chip a COUNT the button
  //     never shows.
  //   - `state === "needs-verification"`: a step-1 `needs-input` issue is
  //     cleared by this card's OWN verdict dropdown, not by that button
  //     (`issueReasonKey`'s own step-1 hints), and `ready` by neither.
  //   - `conformAvailable`: the button is DISABLED while the Verordnung
  //     carries a sent Rückfrage or an unresolved scope (`blockedByInquiry`;
  //     a merely drafted one no longer blocks, TUR-1581), and
  //     "the button already says it" is only true while it can be pressed.
  //     Without this the reviewer lost the last hint that the statement is
  //     unconfirmed in exactly the state where nothing could clear it
  //     ((review), TUR-1211 wave 6). The caller decides what "available"
  //     means; see `ReviewMaskPane`'s own wiring for why the button's
  //     "already checked" case is deliberately not part of it.
  // The chevron's tooltip keeps naming the count either way, so the point
  // stays discoverable on the card it belongs to.
  const chipRedundantWithConformButton =
    statementCount === 1 &&
    conformAvailable &&
    openCount === 1 &&
    firstOpenIssue?.step === 1 &&
    firstOpenIssue.state === "needs-verification"
  const openChipText =
    firstOpenIssue && !chipRedundantWithConformButton
      ? openCount > 1
        ? translate("MC_DECL_V2_UNIT_OPEN_CHIP", {
            n: openCount,
            reason: translate(firstOpenReason!.label),
          })
        : translate(firstOpenReason!.label)
      : null

  // Tester feedback 17.09: the card's own number, in the header row's TWO
  // forms. The full
  // "Aussage {i}" while there is room, the bare number once the row is
  // squeezed — the same `.sv2-hlong`/`.sv2-hshort` swap the count pill and
  // the Geltung label already use inside the `.sv2-hsent` size container
  // (studioV2.css, `@container (max-width: 530px)`), not a second mechanism
  // of its own. The number NEVER leaves: the open-points map, the approve
  // gate and the mark popover all address this card as "Aussage {i}", so a
  // header that dropped it would leave those buttons pointing at nothing
  // the reviewer can see.
  const unitLabel = (
    <span className="sv2-unit-label">
      {/* THE ONE ANNOUNCEMENT, and it is neither of the visual forms below.
          Both of those are always in the DOM with the container query picking
          one, so leaving either in the accessibility tree reads the number
          twice ("Aussage 22") — but hiding only the short form loses it
          altogether under 530px, where the CSS hides the long one and
          `display: none` takes it out of the tree as well. A
          screen-reader-only copy of the FULL label says it exactly once at
          every width. Not an `aria-label` on this wrapper: it is a plain
          span, and a name on a generic role is not reliably announced. */}
      <span className="sr-only">
        {translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })}
      </span>
      <span className="sv2-hlong" aria-hidden="true">
        {translate("MC_DECL_V2_UNIT_LABEL", { i: index + 1 })}
      </span>
      <span className="sv2-hshort" aria-hidden="true">
        {index + 1}
      </span>
    </span>
  )

  const baseGeltung = deriveGeltung({
    scopeAll: card.scopeAll,
    selectedCount: card.articleNumbers.length,
    unresolvedRefs: card.unresolvedRefs,
    inquiryOpen: false, // wired once the inquiry read lands (later PR)
    // Collected questions on this statement persist server-side, so unlike
    // `manualGeltung` this reading survives a sheet, a regulation switch
    // and a reload (live test L2, wave 5).
    draftQuestions: card.draftQuestions === true,
  })
  // `group`/`material` are a local READING of a `list` scope (no write of
  // their own); `ask` opens the placeholder card. Neither survives a real
  // server round-trip, so this resets to `null` (falls back to `baseGeltung`)
  // whenever the card's own identity changes underneath it.
  const [manualGeltung, setManualGeltung] = useState<Geltung | null>(null)
  const displayGeltung = manualGeltung ?? baseGeltung

  const hasPurchaseLink =
    displayGeltung === "all" || card.scopeAll || card.articleNumbers.length > 0

  // Hoisted above the focus effect (which reads it): the STOFFE row/dialog
  // only exists while the verdict names an exception, same gate the render
  // below uses. `hasConditionsRow` is the ONE predicate; `ReviewMaskPane`
  // reuses it to close an already-open substances sheet the moment a
  // verdict edit moves this card off the list.
  const showConditions = hasConditionsRow(card.verdict)

  const [localExpanded, setLocalExpanded] = useState(false)
  const expanded = expandedProp ?? localExpanded
  const setExpanded = (next: boolean | ((prev: boolean) => boolean)) => {
    const value = typeof next === "function" ? next(expanded) : next
    if (onExpandedChange) onExpandedChange(value)
    else setLocalExpanded(value)
  }
  const [quotesOpen, setQuotesOpen] = useState(false)
  useEffect(() => {
    if (!focused) return
    setExpanded(true)
    // W5-4: the step the jump named also opens ITS OWN sheet, so a
    // reviewer sent to "fix the scope"/"check the condition fields" lands
    // IN the browser/editor, not merely on the card that owns it. Step 1 (or
    // no step) only expands, same as before this prop existed. Step 2 opens
    // the article browser through the same callback the pill/Geltung pick
    // use, so it silently no-ops when the caller has no pane to host it
    // (`onOpenArticleBrowser` undefined). Step 3 opens the substances sheet
    // the same way, through `onOpenSubstances`, only while `showConditions`
    // is true: a card with no STOFFE row has nothing to hang the sheet's
    // focus on.
    if (focusStep === 2) onOpenArticleBrowser?.({ kind: "all" }, displayGeltung)
    if (focusStep === 3 && showConditions) onOpenSubstances?.(null)
    // `focusStep` rides in the deps deliberately: `ReviewMaskPane` bumps
    // `focusNonce` on every jump alongside it (`StudioV2Layout::goToOpenPoint`),
    // so the two always change together: this never re-opens a sheet on an
    // unrelated re-render, only on an actual jump. `showConditions` is
    // deliberately NOT a dep: it must gate only a FRESH focus (the effect
    // above), never resurrect a stale step-3 request on a plain verdict
    // change made later, with no new focus/nonce.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused, focusNonce, focusStep])

  const handleVerdictChange = (v: Verdict) => {
    editVerdict.mutate({
      declarationId: declId,
      statementId: card.id,
      verdict: v,
      listVersion: card.listVersion,
    })
  }

  // BUG A (never-stuck): the ONLY server op that flips `statement_status`
  // away from `rejected` is `useReviewStatement`'s "accept" action (there is
  // no dedicated "un-reject"/reset endpoint — verified against
  // `mc_declaration_review_service.py::review_statement`, which accepts
  // accept|reject with no status guard and no pending reset). This therefore
  // lands the statement as ACCEPTED (step-1 signed), never back at pending —
  // the reviewer changes the verdict afterwards if that is not what they
  // meant. The earlier "excluded" override note (`RemoveStatementControl`'s
  // own write, kind `"excluded"`) is NOT cleared — `overrides.ts` has no
  // delete/reset kind for it — so it stays standing as audit history
  // alongside the now-live statement; that is intentional, not a bug.
  const handleRestore = async () => {
    try {
      await reviewStatement.mutateAsync({
        declarationId: declId,
        statementId: card.id,
        action: "accept",
      })
      toast.success(translate("MC_DECL_V2_UNIT_RESTORE_SUCCESS"))
    } catch {
      toast.error(translate("MC_DECL_V2_UNIT_RESTORE_FAILED"))
    }
  }

  // Finding 7 (HIGH, claude-bot): "list" now joins "group"/"material"/"ask"
  // as a LOCAL, no-write display switch, not the immediate-write branch
  // below. It used to fall through to `scopeWriteFor("list", …)` with
  // `selection: card.articleNumbers` — CONTRACTUALLY EMPTY right after a
  // genuinely `scope_all` statement (`StatementGroupEntry`'s own doc), so
  // switching "Alle Artikel" → "Artikelliste" persisted an EMPTY subset on
  // one click, no staging: `deriveFrameworkVerdict`'s `inScope` then drops
  // the statement entirely, silently flipping a non-compliant framework to
  // (vacuously) compliant. `scopeWriteFor` itself now also refuses an empty
  // selection (belt-and-braces — see its own doc), but the real fix is
  // here: the reviewer must choose articles FIRST, through the article
  // browser's staged-then-Apply table (`ArticleBrowserSheet`) or the inline
  // Mine/Not-mine toggle (`group`/`material`), both write directly via
  // `useUpdateStatementScope`, never through this handler. Picking
  // "Artikelliste" opens the browser right away, so the reviewer lands on
  // the table, not on a card that merely changed its label.
  const handleGeltungChange = (next: Geltung) => {
    if (
      next === "group" ||
      next === "material" ||
      next === "ask" ||
      next === "list"
    ) {
      setManualGeltung(next)
      if (next === "list") onOpenArticleBrowser?.({ kind: "all" }, next)
      return
    }
    // next === "all" — the one Geltung with no staged/Apply UI of its own
    // (see `scopeWriteFor`'s "one-way door" doc); this stays the sole
    // immediate write.
    setManualGeltung(null)
    const write = scopeWriteFor(next, {
      current: baseGeltung,
      poolNumbers,
      selection: card.articleNumbers,
    })
    if (!write) return
    if (write.kind === "pool-snapshot") {
      applyPool.mutate({
        declarationId: declId,
        statementId: card.id,
        poolNumbers: write.articleNumbers,
      })
    } else {
      updateScope.mutate({
        declarationId: declId,
        statementId: card.id,
        articleNumbers: write.articleNumbers,
      })
    }
  }

  const evidenceCount = isWorkbook
    ? (card.cells ?? []).length
    : card.evidence.length

  const evidenceList = evidenceCount > 0 && onJumpToEvidence && (
    <EvidenceList
      showAll
      evidence={card.evidence}
      cells={card.cells ?? []}
      isWorkbook={isWorkbook}
      onJumpToEvidence={(evidenceIndex) =>
        onJumpToEvidence(card, index, evidenceIndex)
      }
    />
  )

  // "Anywhere on the card header": the evidence jump, a big hit target.
  // Delegated on the header ROW rather than attached to every element in it,
  // so it stays one handler; a click that started on an actual control (a
  // select's trigger, the count pill, the chevron, the remove button, which
  // would otherwise double-fire) is excluded by walking up to the nearest
  // interactive ancestor. Silently inert (no cursor change) when there is
  // nothing to jump to.
  //
  // THE SECOND `closest` CHECK EXISTS BECAUSE OF `GeltungSelect`/
  // `VerdictSelect`'s Radix `Select`: its popup (`SelectContent`) portals to
  // `document.body` — physically OUTSIDE this row's DOM subtree, so it is
  // NOT among `closest("button, select, input")`'s ancestors when an option
  // is clicked. But React's event system bubbles a portaled click through
  // the REACT tree, not the DOM tree (React's own documented portal
  // behaviour) — the click still reaches THIS handler. Without this second
  // check, picking a verdict/scope option would ALSO fire the evidence jump
  // on any card whose header is clickable. `role="listbox"`/`"option"` are
  // Radix's own attributes on that portaled content, so this needs no
  // sv2-specific class to recognise it. The `[role="dialog"]` check does the
  // same for the dialogs the substance chips and the remove control open
  // (they portal too, and React bubbles their clicks through this row).
  const canJumpToEvidence = evidenceCount > 0
  const headerJump =
    canJumpToEvidence && onJumpToEvidence
      ? {
          onClick: (e: React.MouseEvent<HTMLDivElement>) => {
            const target = e.target as HTMLElement
            if (target.closest("button, select, input")) return
            if (
              target.closest(
                '[role="listbox"], [role="option"], [role="dialog"]',
              )
            )
              return
            onJumpToEvidence(card, index, 0)
          },
        }
      : undefined

  // Keyboard/screen-reader path to the same jump `headerJump` gives a mouse:
  // the header row itself is a div, never focusable, so a reviewer who
  // cannot click needed some other way in. This renders with no visual
  // footprint (`sr-only`) inside the same header div, at every render site
  // `headerJump` appears: the header's own click guard above already
  // excludes clicks starting on a `button`, so this button and the header's
  // onClick never double-fire.
  const evidenceJumpButton = headerJump && onJumpToEvidence && (
    <EvidenceJumpButton
      card={card}
      index={index}
      onJumpToEvidence={onJumpToEvidence}
      translate={translate}
    />
  )

  // The one thing every expand gesture does: toggle the card and still
  // flash to its evidence when there is any. Shared by the header row, the
  // open-points chip button and both chevrons, so they can never drift apart
  // (prod test 2026-10-02, F3: the chevron alone left the document on the
  // previous statement's passage).
  const toggleAndJump = () => {
    setExpanded((v) => !v)
    if (canJumpToEvidence && onJumpToEvidence) onJumpToEvidence(card, index, 0)
  }

  // Product ask (2026-09-17): the header row's whole background, not just
  // the small chevron, expands/collapses the OPEN card, same gesture as
  // `headerJump` above (a click that started on an actual control never
  // reaches here). Also still fires the evidence jump (`headerJump`'s own
  // reasoning), so the flash-to-evidence gesture is not lost, just no
  // longer the ONLY thing a header click does. `[role="combobox"]` names
  // the Radix Select triggers explicitly (they are `button`s too, already
  // caught by the first clause); `input`/`textarea`/`a`/`[role="menuitem"]`
  // guard controls this row does not carry today but could grow. Only used
  // on the open, non-excluded row below; the excluded quiet row keeps its
  // own explicit ▾ button as the sole expand gesture (`headerJump` there is
  // unchanged).
  const handleHeaderRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement
    if (
      target.closest(
        'button, [role="combobox"], input, textarea, a, [role="menuitem"], [role="listbox"], [role="option"], [role="dialog"]',
      )
    )
      return
    toggleAndJump()
  }

  const total = poolNumbers.length
  const count = card.scopeAll ? total : card.articleNumbers.length
  // An unresolved scope with nothing bound reads "Unklare Artikel", never
  // "0 von 330 Artikeln sind … konform" (which says "none are compliant").
  const unclear = displayGeltung === "ask" && !card.scopeAll && count === 0
  const subjectCount = headSubjectCount({
    count,
    total,
    all: card.scopeAll,
    unclear,
  })
  // The pill opens the article browser everywhere the reviewer may pick
  // articles, the "Rückfrage" reading included (Pablo, 2026-09-16): a
  // follow-up covers the articles no other statement does, and those are
  // picked here like any other selection; the composer then asks about
  // exactly them.
  const canPickArticles = !!onOpenArticleBrowser

  // B1: a rejected statement (`card.excluded`, derived server-side — never a
  // client flag) always starts collapsed to a quiet row with a badge in place
  // of the sentence's selects, never rendered indistinguishably from a live
  // card. No RemoveStatementControl here: the statement is already excluded,
  // and that gesture is one-way ("Aussage entfernen" has no undo), so
  // offering it again would be noise at best and confusing at worst. The
  // expand affordance still opens the full, editable card below (read-only
  // is not required, nothing here blocks it).
  //
  // "Wiederherstellen" IS offered here (never-stuck fix): before this, a
  // rejected statement had NO path back — see `handleRestore`'s own doc for
  // why it lands ACCEPTED, not pending, and why the "excluded" override note
  // stays standing as audit history rather than being cleared.
  if (card.excluded && !expanded) {
    return (
      <div
        data-testid="unit-card"
        data-excluded="true"
        className="sv2-exrow sv2-unit sv2-exoff sv2-excol opacity-70"
      >
        <div
          className={cn("sv2-exr1 sv2-hsent", headerJump && "cursor-pointer")}
          {...headerJump}
        >
          {evidenceJumpButton}
          {unitLabel}
          <CountPill
            count={count}
            total={total}
            all={card.scopeAll}
            unclear={unclear}
            quiet
            translate={translate}
          />
          <span className="sv2-nobez">
            {translate("MC_DECL_V2_UNIT_EXCLUDED_BADGE")}
          </span>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="sv2-exopen"
                  disabled={reviewStatement.isPending}
                  onClick={() => void handleRestore()}
                >
                  {translate("MC_DECL_V2_UNIT_RESTORE")}
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {translate("MC_DECL_V2_UNIT_RESTORE_TITLE")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="sv2-exopen"
                  aria-label={translate("MC_DECL_V2_UNIT_EXPAND")}
                  onClick={toggleAndJump}
                >
                  ▾
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {translate("MC_DECL_V2_UNIT_EXPAND")}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    )
  }

  // The sentence's four tokens, laid out in the language's own order
  // (`headTokenOrder`): German "sind laut Liste konform", English "are
  // compliant per article list".
  const tokens: Record<HeadToken, React.ReactNode> = {
    count: (
      <CountPill
        count={count}
        total={total}
        all={card.scopeAll}
        unclear={unclear}
        warn={!card.scopeAll && !unclear && count === 0}
        title={
          canPickArticles ? translate("MC_DECL_V2_UNIT_SCOPE_PICK") : undefined
        }
        onClick={
          canPickArticles
            ? () => onOpenArticleBrowser?.({ kind: "all" }, displayGeltung)
            : undefined
        }
        translate={translate}
      />
    ),
    verb: (
      <span className="sv2-hw sv2-hverb">
        {headVerb(subjectCount, translate)}
      </span>
    ),
    geltung: (
      <GeltungSelect
        value={displayGeltung}
        onChange={handleGeltungChange}
        disabled={updateScope.isPending || applyPool.isPending}
        aria-busy={card.saving}
        triggerLabel={geltungSentence(displayGeltung, translate)}
      />
    ),
    verdict: (
      <VerdictSelect
        value={card.verdict}
        onChange={handleVerdictChange}
        disabled={editVerdict.isPending}
        aria-busy={card.saving}
        triggerLabel={verdictSentence(card.verdict, translate)}
      />
    ),
  }

  const toggleLabel = translate(
    expanded ? "MC_DECL_V2_UNIT_COLLAPSE" : "MC_DECL_V2_UNIT_EXPAND",
  )

  // The open-points chip: a real button (keyboard-focusable, Enter/Space
  // fire `onClick` natively), built once so the tooltip-wrapped and plain
  // render paths below never duplicate its JSX.
  const openChipButton = SHOW_OPEN_CHIP && openChipText && (
    <button
      type="button"
      className="sv2-openchip"
      aria-expanded={expanded}
      onClick={toggleAndJump}
    >
      {openChipText}
    </button>
  )

  return (
    <div
      data-testid="unit-card"
      data-excluded={card.excluded || undefined}
      data-expanded={expanded || undefined}
      className={cn(
        "sv2-exrow sv2-unit",
        isBase ? "sv2-unit-base" : !hasPurchaseLink && "sv2-exoff",
        card.excluded && "opacity-70",
      )}
    >
      <div
        className="sv2-exr1 sv2-hsent cursor-pointer"
        onClick={handleHeaderRowClick}
      >
        {evidenceJumpButton}
        {unitLabel}
        {headTokenOrder(translate).map((token) => (
          <Fragment key={token}>{tokens[token]}</Fragment>
        ))}
        {/* TUR-1445's provenance chip: the reviewer, not the pipeline, wrote
            this statement — server-derived from the row's creator, so it
            survives removal notes and re-staging. Long form only: it is the
            first thing a squeezed row can spare. */}
        {card.manuallyAdded && (
          <span className="sv2-manual sv2-hlong">
            {translate("MC_DECL_OV_INCLUDED")}
          </span>
        )}
        <span className="sv2-hright ml-auto">
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="sv2-iconbtn sv2-chev"
                  aria-expanded={expanded}
                  aria-label={toggleLabel}
                  onClick={toggleAndJump}
                >
                  <ChevronDown size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {openCount > 0
                  ? translate("MC_DECL_V2_UNIT_OPEN_POINTS", { n: openCount })
                  : toggleLabel}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          {/* A hand-added statement has no extracted proposal to return to
              (the server answers NO_RESET_TARGET), so it never gets the icon. */}
          {resetEnabled && card.reviewerTouched && !card.manuallyAdded && (
            <ResetStatementButton declId={declId} statementId={card.id} />
          )}
          <RemoveStatementControl
            declId={declId}
            statementId={card.id}
            framework={card.framework}
            groupIndex={card.groupIndex}
          />
        </span>
      </div>

      {/* W5-4: replaces the bare chevron dot, names the count AND the first
          open issue's own reason, so a reviewer scanning the sentence list
          knows WHERE and WHY without opening the card. W5 step3-chip: a
          hover explanation names WHICH gesture closes it, when the V3 mask
          actually has one (`firstOpenReason.hint`, null otherwise:
          `issueReasonKey`'s own doc). W5 overflow fix: lives on its OWN
          line below the header sentence, not inside `.sv2-hright`: the
          one-row header cannot hold it next to the verdict pill without
          collapsing the pill to an unreadable ellipsis ("k…", measured on
          sandbox), and no copy length for this chip fits an in-row shrink.
          Review fix: the chip itself is a real button (`toggleAndJump`),
          keyboard-focusable and Enter/Space-operable; the wrapper line is
          plain layout with no handler of its own. */}
      {openChipButton && (
        <div className="sv2-exr-open">
          {firstOpenReason?.hint ? (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>{openChipButton}</TooltipTrigger>
                <TooltipContent side="top" className="max-w-[280px]">
                  {translate(firstOpenReason.hint)}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            openChipButton
          )}
        </div>
      )}

      {/* MC_ISSUER_SCOPE_PROPOSAL: collapsed, the sentence alone reads as
          every article covered, and the card is what the reviewer confirms.
          Expanded, `ArticleRow` carries the same line. */}
      {!expanded && card.scopeAll && hasIssuerScopeNote(issuerProposal) && (
        <div className="sv2-exr-note">
          <IssuerScopeCardNote
            proposal={issuerProposal}
            onOpenArticleBrowser={
              onOpenArticleBrowser &&
              ((view: BrowserView) =>
                onOpenArticleBrowser(view, displayGeltung))
            }
          />
        </div>
      )}

      {expanded && (
        <>
          <EditionRow declId={declId} card={card} editionInfo={editionInfo} />

          <ArticleRow
            declId={declId}
            card={card}
            pool={pool}
            geltung={displayGeltung}
            // `manualGeltung === "ask"` is LOCAL display state: it never
            // flips the framework's own derivation, so `ReviewMaskPane`'s
            // `derivation`-gated mount of the same coachmark never fires for
            // it (`R1Coachmark`'s own doc); the row force-mounts it there
            // instead. ONLY for that manual case: when `baseGeltung` itself
            // is "ask" the framework IS R1 and `ReviewMaskPane` already
            // mounts the coachmark, so a forced copy here would be a second,
            // independently dismissible one per unresolved statement
            // (review bot, PR #2014).
            forceCoachmark={manualGeltung === "ask" && baseGeltung !== "ask"}
            uncoveredClaimsByArticle={uncoveredClaimsByArticle}
            readerHintsByArticle={readerHintsByArticle}
            onJumpToSheetRow={onJumpToSheetRow}
            onOpenArticleBrowser={
              onOpenArticleBrowser &&
              ((view: BrowserView) =>
                onOpenArticleBrowser(view, displayGeltung))
            }
            editionInfo={editionInfo}
            frameworkName={frameworkName}
            issuerProposal={issuerProposal}
          />

          {showConditions && (
            <SubstanceRow
              declId={declId}
              card={card}
              onOpenEditor={onOpenSubstances}
            />
          )}

          {evidenceList && (
            <div className="sv2-uslot sv2-dark">
              <button
                type="button"
                className="cursor-pointer text-[11px] font-semibold text-grey5 underline decoration-dotted underline-offset-4 hover:text-white focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
                aria-expanded={quotesOpen}
                onClick={() => setQuotesOpen((v) => !v)}
              >
                {quotesOpen
                  ? translate("MC_DECL_V2_UNIT_QUOTES_HIDE")
                  : translate("MC_DECL_V2_UNIT_QUOTES_SHOW", {
                      n: evidenceCount,
                    })}
              </button>
            </div>
          )}
          {quotesOpen && evidenceList}
        </>
      )}
    </div>
  )
}

/** The `headerJump` gesture's keyboard/screen-reader equivalent: an actual,
 *  focusable `button`, visually hidden (`sr-only`) so it adds no chip back
 *  to the collapsed row. Lives inside the same `sv2-exr1` header div as
 *  `headerJump` at every render site, so the header's own click guard
 *  (`target.closest("button, …")`) already keeps a click on this button
 *  from also firing the header's onClick. */
function EvidenceJumpButton({
  card,
  index,
  onJumpToEvidence,
  translate,
}: {
  card: StatementCard
  index: number
  onJumpToEvidence: (
    card: StatementCard,
    index: number,
    evidenceIndex?: number,
  ) => void
  translate: (
    key: TranslationKeys,
    v?: Record<string, string | number>,
  ) => string
}) {
  return (
    <button
      type="button"
      className="sr-only"
      onClick={() => onJumpToEvidence(card, index, 0)}
    >
      {translate("MC_DECL_V2_EVIDENCE_JUMP")}
    </button>
  )
}
